import React, { useMemo, useState } from "react";
import { toast, Toaster } from "sonner";
import { Handshake, TrendingUp, IndianRupee, Users, ArrowRight, Percent } from "lucide-react";
import DebtNavbar from "../components/layout/DebtNavbar";
import Footer from "../components/layout/Footer";
import Seo from "../components/Seo";
import api, { formatApiError } from "../lib/api";

/**
 * Public "Become a Partner" landing — for DSAs and consultants.
 *
 * Shows commission structure + an interactive earnings calculator,
 * then a lead form that hits `POST /api/public/become-partner`.
 * The private CP portal at `/partner/dashboard` remains unchanged.
 */

const PRODUCTS_LIST = ["Home Loan", "Personal Loan", "Business Loan", "LAP", "Working Capital", "Structured Finance"];
const VOLUME_BANDS = [
  { key: "1_5",   label: "₹1 – 5 Cr / month",    payout_pct: 0.5 },
  { key: "5_25",  label: "₹5 – 25 Cr / month",   payout_pct: 0.65 },
  { key: "25_100",label: "₹25 – 100 Cr / month", payout_pct: 0.8 },
  { key: "100_plus", label: "₹100 Cr+ / month",  payout_pct: 1.0 },
];

const fmtRup = (n) => n?.toLocaleString("en-IN", { maximumFractionDigits: 0 }) || "0";

export default function DebtBecomePartner() {
  const [monthly, setMonthly] = useState(10); // ₹ Cr/month
  const [form, setForm] = useState({
    name: "", mobile: "", email: "", city: "", state: "",
    current_business: "", expected_volume: "5_25", products: [], message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const activeBand = useMemo(() => {
    if (monthly < 5)   return VOLUME_BANDS[0];
    if (monthly < 25)  return VOLUME_BANDS[1];
    if (monthly < 100) return VOLUME_BANDS[2];
    return VOLUME_BANDS[3];
  }, [monthly]);

  const monthlyPayout = useMemo(
    () => (monthly * 1e7 * activeBand.payout_pct) / 100,
    [monthly, activeBand]
  );

  const toggleProduct = (p) => {
    setForm((f) => ({ ...f, products: f.products.includes(p) ? f.products.filter((x) => x !== p) : [...f.products, p] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post("/public/become-partner", form);
      setDone(r.data?.application_id);
      toast.success("Application submitted — we'll be in touch.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen text-ink bg-cream" data-testid="become-partner-page">
      <Seo title="Become a Partner · VenturaZ MONEY" description="Earn 0.5–1% on every disbursed loan. Full lender panel, dedicated relationship manager, T+7 payouts." path="/venturaz-money/become-partner" />
      <Toaster richColors position="top-right" />
      <DebtNavbar />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #1F1408 0%, #3E2410 45%, #7A4E1D 100%)" }}>
        <div className="pointer-events-none absolute -bottom-32 -left-32 w-[520px] h-[520px] rounded-full opacity-25" style={{ background: "radial-gradient(circle, #E9C46A 0%, transparent 60%)" }} />
        <div className="relative max-w-6xl mx-auto px-6 py-14 lg:py-20 text-[#FFF3D9]">
          <div className="inline-flex items-center gap-1.5 bg-gold text-[#1F1408] text-[10px] font-mono uppercase tracking-[0.22em] font-bold px-2.5 py-1 rounded-full mb-4">
            <Handshake size={11} /> DSA / Consultant Program
          </div>
          <h1 className="font-display text-4xl md:text-5xl leading-tight max-w-3xl mb-3">Earn on every disbursed loan.</h1>
          <p className="text-[#FFF3D9]/80 text-lg leading-relaxed max-w-2xl mb-8">One panel of 114 lenders. One credit team backing you. Full ownership of your book — we&apos;re the rails, you&apos;re the relationship.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat icon={IndianRupee} k="0.5–1%" label="Payout on disbursal" />
            <Stat icon={TrendingUp} k="T+7" label="Payout cadence" />
            <Stat icon={Users} k="Named RM" label="Per-partner ops" />
            <Stat icon={Percent} k="100%" label="Own the client" />
          </div>
        </div>
      </section>

      {/* Earnings calculator */}
      <section className="max-w-6xl mx-auto px-6 py-14" data-testid="partner-earnings">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A4E1D] mb-2">Earnings calculator</div>
        <h2 className="font-display text-2xl md:text-3xl leading-tight mb-6 max-w-2xl">If you disburse ₹{monthly} Cr / month, you earn:</h2>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-2xl border border-[#B8860B]/25 bg-white p-6">
            <label className="block mb-2">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">Monthly disbursal volume</div>
                <div className="text-sm font-heading text-[#7A4E1D]" data-testid="partner-monthly-value">₹{monthly} Cr</div>
              </div>
              <input type="range" min={1} max={200} step={1} value={monthly} onChange={(e) => setMonthly(Number(e.target.value))} className="w-full accent-[#7A4E1D]" data-testid="partner-monthly-slider" />
              <div className="flex justify-between text-[10px] font-mono text-ink-muted mt-1">
                <span>₹1 Cr</span><span>₹100 Cr</span><span>₹200 Cr</span>
              </div>
            </label>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-2">
              {VOLUME_BANDS.map((b) => (
                <div
                  key={b.key}
                  className={`p-3 rounded-lg border ${b === activeBand ? "border-[#7A4E1D] bg-[#7A4E1D]/[0.06]" : "border-[#B8860B]/20 bg-white"}`}
                  data-testid={`partner-band-${b.key}`}
                >
                  <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">{b.label}</div>
                  <div className="text-sm font-heading text-[#7A4E1D]">{b.payout_pct}% payout</div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl p-6 text-[#FFF3D9]" style={{ background: "linear-gradient(135deg, #1F1408 0%, #7A4E1D 100%)" }} data-testid="partner-earnings-summary">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#E9C46A] mb-2">Estimated monthly payout</div>
            <div className="font-display text-4xl leading-none mb-4">₹{fmtRup(monthlyPayout)}</div>
            <div className="text-[13px] text-[#FFF3D9]/80 leading-relaxed mb-6">At {activeBand.payout_pct}% on ₹{monthly} Cr of monthly disbursals. Actual payouts depend on product-mix and lender scheme.</div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#E9C46A]">Annualised · ₹{fmtRup(monthlyPayout * 12)}</div>
          </div>
        </div>
      </section>

      {/* Application form */}
      <section className="max-w-3xl mx-auto px-6 pb-16" data-testid="partner-application">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A4E1D] mb-2">Apply to onboard</div>
        <h2 className="font-display text-2xl md:text-3xl leading-tight mb-6">Two minutes. One form. We call back within a working day.</h2>

        {done ? (
          <div className="rounded-2xl border border-[#B8860B]/25 bg-white p-6" data-testid="partner-success">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A4E1D] mb-2">Application received</div>
            <div className="font-heading text-ink text-xl mb-1">Ref · {done}</div>
            <div className="text-[13px] text-ink-soft mb-4">Our channel-partner team will reach out within one business day. You&apos;ll get onboarding docs on the number you shared.</div>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl border border-[#B8860B]/25 bg-white p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="partner-name" />
              <Field label="Mobile" required value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} testId="partner-mobile" />
              <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="partner-email" />
              <Field label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testId="partner-city" />
              <Field label="State" value={form.state} onChange={(v) => setForm({ ...form, state: v })} testId="partner-state" />
              <div>
                <label className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-1 block">Expected monthly volume</label>
                <select value={form.expected_volume} onChange={(e) => setForm({ ...form, expected_volume: e.target.value })} className="w-full px-3 py-2 border border-[#B8860B]/25 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#7A4E1D]" data-testid="partner-volume">
                  {VOLUME_BANDS.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
                </select>
              </div>
            </div>
            <Field label="Current business / role" value={form.current_business} onChange={(v) => setForm({ ...form, current_business: v })} testId="partner-current-business" />
            <div>
              <label className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-1 block">Products you plan to sell</label>
              <div className="flex flex-wrap gap-2">
                {PRODUCTS_LIST.map((p) => (
                  <button type="button" key={p} onClick={() => toggleProduct(p)} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${form.products.includes(p) ? "bg-[#7A4E1D] text-[#FFF3D9] border-[#7A4E1D]" : "border-[#B8860B]/30 text-[#7A4E1D] hover:bg-[#7A4E1D]/10"}`} data-testid={`partner-product-${p.replace(/\s+/g, "-")}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-1 block">Anything else? (optional)</label>
              <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-3 py-2 border border-[#B8860B]/25 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#7A4E1D] resize-none" data-testid="partner-message" />
            </div>
            <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#7A4E1D] text-[#FFF3D9] text-sm font-heading font-bold hover:bg-[#5D3B15] disabled:opacity-50 transition-colors" data-testid="partner-submit">
              {submitting ? "Submitting…" : "Submit application"} <ArrowRight size={14} />
            </button>
          </form>
        )}
      </section>
      <Footer />
    </div>
  );
}

function Stat({ icon: Icon, k, label }) {
  return (
    <div className="rounded-xl border border-[#B8860B]/25 bg-white/10 backdrop-blur p-4">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-7 h-7 rounded-md bg-gold text-[#1F1408] inline-flex items-center justify-center">
          <Icon size={13} />
        </div>
        <div className="font-display text-lg text-[#FFF3D9]">{k}</div>
      </div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFF3D9]/70">{label}</div>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", testId }) {
  return (
    <div>
      <label className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-1 block">{label}{required && <span className="text-[#7A4E1D]">*</span>}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="w-full px-3 py-2 border border-[#B8860B]/25 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#7A4E1D]" data-testid={testId} />
    </div>
  );
}
